import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import { logOut, setCredentials } from "../auth/authSlice";

// Production uses a relative URL so it works on any domain whose nginx
// proxies /core/api/ to b-core (recreation, mee-recreation, etc.).
const ENV_TO_URL = {
  production: "/core/api/", // Coolify production — same-domain path-based routing
  staging: "https://cc-cegi-198.inesctec.pt/core/api/", // CEGI staging
  development: "http://10.61.6.210/core/api/", // Dev server
  local: "http://localhost:8000/core/api/", // Local development
  k8s_beflex: "https://recreation.e4157ab.kyma.ondemand.com/core/api/",
  k8s_sap_store: "https://recreation.fea9c33.kyma.ondemand.com/core/api/",
};

const ENV = process.env.REACT_APP_ENV || "development";
const API_BASE_URL = process.env.REACT_APP_API_URL || ENV_TO_URL[ENV];

const baseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
  credentials: "include",
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth.accessToken;

    if (token) {
      headers.set("authorization", `Bearer ${token}`);
    }
    return headers;
  },
});
const baseQueryWithReauth = async (args, api, extraOptions) => {
  // send baseQuery
  // console.log("Fetching:", args);
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result?.error.status === 401) {
    // Don't try to refresh on login endpoint or if no refresh token exists
    const refreshToken = localStorage.getItem("refreshToken");
    const isLoginEndpoint =
      typeof args === "string" ? args.includes("/auth/login") : args.url?.includes("/auth/login");

    if (!refreshToken || isLoginEndpoint) {
      // No refresh token available or this is a login request - just return the error
      return result;
    }

    // send refresh token to get new access token
    const refreshResult = await baseQuery(
      {
        url: "/auth/refresh",
        method: "POST",
        body: { refresh_token: refreshToken },
      },
      api,
      extraOptions
    );

    if (refreshResult.error && refreshResult.error.status === 403) {
      refreshResult.error.data.message = "Your login has expired. ";
      api.dispatch(logOut());

      return refreshResult;
    }

    if (refreshResult.data) {
      const { user } = api.getState().auth;

      // store the new token
      api.dispatch(setCredentials({ ...refreshResult.data, user }));

      // retry the original query with a new access token
      result = await baseQuery(args, api, extraOptions);
    } else {
      api.dispatch(logOut());
      return refreshResult;
    }

    // Return the retried request's outcome, not the refresh call's: returning
    // refreshResult made any endpoint that still errors after a token refresh
    // (e.g. 401 "invalid GDBN credentials") look like a successful mutation.
    return result;
  }

  return result;
};

// Exported so non-page consumers (e.g. useTaskStatusEffect) can force a global
// soft refetch of every active query by invalidating all registered tags.
export const API_TAG_TYPES = [
  "Community",
  "BusinessValidity",
  "Member",
  "Resource",
  "SizingGeneralParameter",
  "MeterParameters",
  "SizingMeterStaticParameter",
  "OpportunityCost",
  "SizingOptimization",
  "FlexibilityBids",
  "SizingSimulation",
  "Meter",
  "Contract",
  "OMIE",
  "ERSE",
  "Load",
  "Allocation",
  "DSO",
  "RESP",
  "Metrics",
  "Files",
  "Users",
  "Permissions",
  "Coefficient",
  "TransactionAdjustments",
  "Settlement",
  "EV",
  "Batteries",
  "Setpoints",
  "LoadsForecast",
  // Forecast table/queries tag themselves "LoadForecast" (singular); register it
  // so its invalidation (and the global post-upload refetch) actually reaches them.
  "LoadForecast",
  "EWH",
  "EMSConfig",
  "EMSModules",
  "EMSModuleSchema",
  "EMSOffer",
  "EMSSetpoint",
  "EMSMemberEnergy",
  "EMSBaseline",
  "Integration",
  "IntegrationServices",
  "IntegrationServiceAuth",
];

const apiSlice = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  tagTypes: API_TAG_TYPES,
  endpoints: (/* builder */) => ({}),
});

export default apiSlice;
